import { configureLogging, createLogger, LOG_NAMESPACES } from './logger';

const logger = createLogger(LOG_NAMESPACES.APP);

let apiHost: string;

// Determine the API host based on the current environment
if (window.location.host.includes('localhost')) {
  // In local development, leave apiHost empty so requests go through the Vite dev proxy
  // (configured in vite.config.ts), which forwards /api/* to the backend and avoids CORS issues.
  // Set VITE_RUBIDEX_BE only if you want to bypass the proxy and hit the backend directly.
  apiHost = import.meta.env.VITE_RUBIDEX_BE || '';
  logger.info('Using Vite proxy for API host in local development', {
    apiHost,
  });
} else {
  // Use the same protocol and URL as the current host (works for both http and https)
  apiHost = `${window.location.protocol}//${window.location.host}`;
  logger.info('Production environment detected', { apiHost });
}

// Configure logging based on environment and API host
configureLogging();

if (import.meta.env.DEV) {
  // Log the API host and all environment variables in development mode
  logger.debug('Development environment configuration', {
    apiHost,
    allEnvVars: import.meta.env,
  });
}

/**
 * Determines whether notification polling is enabled.
 *
 * - `"true"`  → enabled
 * - `"false"` → disabled
 * - empty / undefined → falls back to NODE_ENV: production = true, development = false
 */
const resolveNotificationsPolling = (): boolean => {
  const envValue = import.meta.env.VITE_ENABLE_NOTIFICATIONS_POLLING?.trim();

  if (envValue === 'true') {
    return true;
  }
  if (envValue === 'false') {
    return false;
  }

  // Empty or not set → derive from environment mode
  return import.meta.env.PROD;
};

const isNotificationsPollingEnabled = resolveNotificationsPolling();

logger.info('Notifications polling', {
  enabled: isNotificationsPollingEnabled,
  envValue: import.meta.env.VITE_ENABLE_NOTIFICATIONS_POLLING,
});

/**
 * Default export containing configuration settings for the application.
 *
 * @property {string} apiHost - The base URL or hostname for the API.
 * @property {boolean} isNotificationsPollingEnabled - Whether notification polling is active.
 */
export default {
  apiHost,
  isNotificationsPollingEnabled,
};
